/**
 * Los clientes de un negocio (spec 015 · FR-017…FR-019).
 *
 * Un cliente aquí es **un contacto de Chatwoot**. No hay tabla de clientes en el
 * panel y no debería haberla: quien manda sobre quién es una persona es el
 * canal, porque es donde llega el mensaje.
 *
 * LO QUE ESTA PANTALLA ENSEÑA Y NO SE VE EN NINGÚN OTRO SITIO
 * ──────────────────────────────────────────────────────────
 * **Quién no está atado a la agenda.** Un contacto sin `ea_customer_id` es
 * alguien a quien el asistente le dirá *«no tienes ninguna cita»* teniéndola. Es
 * de los errores más caros del producto y hoy no se ve en ningún lado.
 *
 * Y de paso, lo que sale solo al mirarlos juntos: **los duplicados**. Tres
 * «José Martín Rodríguez» con tres teléfonos distintos son tres historiales a
 * medias, y buscar antes de crear es lo único que lo evita.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { api } from '../../lib/api'
import { useEsAdminUi } from '../../ver-como'

interface Cliente {
    id: number
    nombre: string
    telefono: string | null
    email: string | null
    eaCustomerId: number | null
    consentimiento: boolean
    ficticio: boolean
    /** `null` = no se pudo preguntar a la agenda. Distinto de cero. */
    citas: number | null
    proximaCita: string | null
}

interface Respuesta {
    accountId: number
    canal: { alcanzable: boolean; motivo: string | null }
    clientes: Cliente[]
    total: number
    sinAtarEnEstaPagina: number
}

/** `2026-08-20 13:00` → `20 ago · 13:00`. */
function alarmaSinAtar(c: Cliente): boolean {
    return c.eaCustomerId === null && c.citas !== null && c.citas > 0
}

function proximaCorta(v: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2})/.exec(v)
    if (!m) return v
    const d = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00Z`)
    return `${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} · ${m[4]}`
}

export function ClientesNegocio() {
    const { accountId } = useParams<{ accountId: string }>()
    // La búsqueda puede venir por URL: es lo que permite llegar aquí desde el
    // registro de movimientos, ya buscando a la persona de esa cita.
    const [params] = useSearchParams()
    const inicial = params.get('busca') ?? ''
    const [texto, setTexto] = useState(inicial)
    // Lo que se busca de verdad. Separado de lo que se teclea para no lanzar una
    // petición por letra contra el canal de un cliente.
    const [busca, setBusca] = useState(inicial)

    // La URL manda mientras cambie. Sin esto, llegar aquí desde DOS movimientos
    // distintos del registro —misma ruta, otro `?busca=`— dejaba la primera
    // búsqueda puesta: React no vuelve a montar el componente.
    useEffect(() => {
        setTexto(inicial)
        setBusca(inicial)
    }, [inicial])

    const qs = busca ? `?busca=${encodeURIComponent(busca)}` : ''
    const { data, isPending, isError } = useQuery<Respuesta>({
        queryKey: ['clientes', accountId, busca],
        queryFn: () => api.get<Respuesta>(`/business/${accountId}/clientes${qs}`),
    })
    const esAdmin = useEsAdminUi()
    const qc = useQueryClient()
    const atar = useMutation({
        mutationFn: () =>
            api.post<{ atados: number; omitidos: number; fallidos: number }>(
                `/business/${accountId}/clientes/atar`,
                {},
            ),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ['clientes', accountId] }),
    })

    return (
        <div className="space-y-4">
            <header className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-neutral-100">Clientes</h2>
                    <p className="text-xs text-neutral-500">
                        Las personas que han escrito a este negocio. Son los contactos del canal.
                    </p>
                </div>
            </header>

            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    setBusca(texto.trim())
                }}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900/40 p-3"
            >
                <input
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder="Teléfono, nombre o correo"
                    aria-label="Buscar cliente"
                    className="min-w-0 w-full flex-1 rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600"
                />
                <button
                    type="submit"
                    className="min-h-11 rounded-md bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-900 hover:bg-white md:min-h-0"
                >
                    Buscar
                </button>
                {busca && (
                    <button
                        type="button"
                        onClick={() => {
                            setTexto('')
                            setBusca('')
                        }}
                        className="rounded-md border border-neutral-800 px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-100"
                    >
                        Quitar la búsqueda
                    </button>
                )}
            </form>

            {isPending && <p className="text-sm text-neutral-500">Consultando el canal…</p>}
            {isError && <p className="text-sm text-rose-400">No se pudo consultar el canal.</p>}

            {/* Igual que en el calendario: una lista vacía y un canal caído se ven
                exactamente igual, y quien mira concluye «este negocio no tiene
                clientes». Cuando no se pudo preguntar, se dice y no se pinta lista. */}
            {data && !data.canal.alcanzable && (
                <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-6 text-center">
                    <p className="text-sm font-medium text-amber-300">No se ha podido leer el canal</p>
                    <p className="mt-1 text-xs text-amber-200/70">{data.canal.motivo}</p>
                    <p className="mt-3 text-[11px] text-neutral-500">
                        No se muestra ninguna lista: estaría vacía y parecería que este negocio no
                        tiene clientes.
                    </p>
                </div>
            )}

            {data?.canal.alcanzable && (
                <>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-neutral-500">
                        {/* El total y lo que se ve, por separado y siempre. Un «3 sin
                            atar» junto a 45 clientes se lee como un total cuando es
                            una página de quince. */}
                        <span>
                            <strong className="text-neutral-300">{data.total}</strong>{' '}
                            {busca ? 'coinciden' : 'en total'}
                        </span>
                        {data.clientes.length < data.total && (
                            <span>se ven los {data.clientes.length} más recientes</span>
                        )}
                        {data.clientes.filter(alarmaSinAtar).length > 0 && (
                            <span className="text-amber-400">
                                {data.clientes.filter(alarmaSinAtar).length} con cita y sin atar
                            </span>
                        )}
                        {data.sinAtarEnEstaPagina > 0 &&
                            data.clientes.filter(alarmaSinAtar).length === 0 && (
                                <span>
                                    {data.sinAtarEnEstaPagina} sin atar — sin cita, no es un fallo
                                </span>
                            )}
                        {esAdmin && data.sinAtarEnEstaPagina > 0 && (
                            <button
                                type="button"
                                disabled={atar.isPending}
                                onClick={() => atar.mutate()}
                                className="rounded-md border border-amber-500/40 px-2.5 py-1 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50"
                            >
                                {atar.isPending ? 'Atando…' : 'Atar los que se pueda'}
                            </button>
                        )}
                        {atar.isSuccess && (
                            <span className="text-emerald-400">
                                Atados {atar.data.atados}
                                {atar.data.fallidos > 0 ? ` · ${atar.data.fallidos} no` : ''}
                            </span>
                        )}
                    </div>

                    {data.clientes.length === 0 ? (
                        <p className="rounded-lg border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-400">
                            {busca ? 'Nadie coincide con esa búsqueda.' : 'Todavía no ha escrito nadie.'}
                        </p>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-neutral-800">
                            <table className="tabla-carta w-full text-sm">
                                <thead>
                                    <tr className="border-b border-neutral-800 text-left text-[11px] uppercase tracking-wide text-neutral-500">
                                        <th className="px-3 py-2 font-medium">Nombre</th>
                                        <th className="px-3 py-2 font-medium">Teléfono</th>
                                        <th className="px-3 py-2 font-medium">Correo</th>
                                        <th className="px-3 py-2 font-medium">Citas</th>
                                        <th className="px-3 py-2 font-medium">Agenda</th>
                                        <th className="px-3 py-2 font-medium">Condiciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.clientes.map((c) => (
                                        <tr key={c.id} className="border-b border-neutral-900 last:border-b-0">
                                            <td className="px-3 py-2" data-label="Nombre">
                                                <Link
                                                    to={`/business/${accountId}/clientes/${c.id}`}
                                                    className="text-neutral-100 underline decoration-neutral-700 underline-offset-2 hover:decoration-sky-400"
                                                >
                                                    {c.nombre}
                                                </Link>
                                                {c.ficticio && (
                                                    <span
                                                        className="ml-1.5 rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] text-amber-300"
                                                        title="Número ficticio (+345…): esta persona no existe"
                                                    >
                                                        🧪 de prueba
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 tabular-nums text-neutral-300" data-label="Teléfono">
                                                {c.telefono ?? <span className="text-neutral-600">sin teléfono</span>}
                                            </td>
                                            <td className="px-3 py-2 text-neutral-400" data-label="Correo">
                                                {c.email ?? <span className="text-neutral-600">—</span>}
                                            </td>
                                            {/* Solo las FUTURAS. Un total histórico
                                                contesta «¿ha venido mucho?», que es
                                                otra pregunta; aquí se busca «¿tiene
                                                algo pendiente?» — antes de llamar,
                                                antes de crearle otra, antes de
                                                borrarlo. */}
                                            <td className="whitespace-nowrap px-3 py-2" data-label="Citas">
                                                {/* Tres estados y NO dos, porque «no
                                                    lo sé» tiene dos causas distintas
                                                    y la de abajo no es un fallo:
                                                    quien no está atado a la agenda no
                                                    puede tener citas que sepamos. */}
                                                {c.citas === null ? (
                                                    <span
                                                        className="text-amber-400"
                                                        title="No se pudo preguntar a la agenda. No es lo mismo que cero."
                                                    >
                                                        ?
                                                    </span>
                                                ) : c.citas === 0 ? (
                                                    <span className="text-neutral-600">ninguna</span>
                                                ) : (
                                                    <span className="text-neutral-200">
                                                        {c.citas}
                                                        {c.proximaCita && (
                                                            <span className="ml-1.5 text-xs text-neutral-500">
                                                                próx. {proximaCorta(c.proximaCita)}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </td>

                                            <td className="px-3 py-2" data-label="Agenda">
                                                {c.eaCustomerId !== null ? (
                                                    <span className="text-emerald-400">✓ atado</span>
                                                ) : alarmaSinAtar(c) ? (
                                                    <span
                                                        className="text-amber-400"
                                                        title="Tiene cita y el canal no tiene el id: el asistente, por el camino corto, diría que no hay cita"
                                                    >
                                                        ⚠ sin atar
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="text-neutral-500"
                                                        title="No está atado a la agenda. Si solo preguntó, es lo esperado."
                                                    >
                                                        sin atar
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2" data-label="Condiciones">
                                                {c.consentimiento ? (
                                                    <span className="text-neutral-400">aceptadas</span>
                                                ) : (
                                                    <span className="text-neutral-600">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <p className="rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2 text-[11px] text-neutral-400">
                        «Sin atar» no es no tener cita: es que el canal no tiene el id de la ficha
                        de la agenda. Quien solo preguntó (un horario, un precio) queda así, y está
                        bien. El aviso en ámbar es cuando hay cita y no está atada: ahí el
                        asistente, por el camino corto, diría que no hay cita. Atar escribe ese
                        id, sin inventar fichas ni pisar un enlace que ya apunte a otra.
                    </p>
                </>
            )}
        </div>
    )
}
